import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { DatePicker, MaskedInput, Select } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import type { RequestToVacate } from "pages/liens/LienTypes";
import type { FieldErrors } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { DATE_TO_STRING, STRING_TO_DATE } from "utils/date-utils";

type OptionType = {
  value: string;
  label: string;
};

type RequestToVacateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: RequestToVacate) => Promise<void>;
  lienId: number;
  organizationId: number;
  title?: string;
};

const DECISION_OPTIONS = [
  { value: "APPROVED", label: "Approved" },
  { value: "DENIED", label: "Denied" },
  { value: "PENDING", label: "Pending" },
];

const FormErrors = ({ errors }: { errors: FieldErrors<RequestToVacate> }) => {
  if (Object.keys(errors).length === 0) return null;

  return (
    <div className="error-summary">
      {Object.entries(errors).map(([field, error]) => (
        <div key={field} className="error-message">
          {error?.message || `${field} is invalid`}
        </div>
      ))}
    </div>
  );
};

const RequestToVacateModal = ({
  isOpen,
  onClose,
  onSubmit,
  lienId,
  organizationId,
  title = "Request to Vacate",
}: RequestToVacateModalProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<RequestToVacate>({
    defaultValues: {
      lien: lienId,
      organization: organizationId,
      request_to_vacate_due_date: "",
      request_to_vacate_date: "",
      request_to_vacate_amount: "",
      request_to_vacate_decision: "PENDING",
      request_to_vacate_savings: "",
    },
    mode: "onBlur",
  });

  const handleFormSubmit = handleSubmit(async (data) => {
    try {
      await onSubmit(data);
      onClose();
      reset();
    } catch (error) {
      console.error("Form submission error:", error);
    }
  });

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={onClose}
      footer={
        <div className="flex jc-c">
          <Button onClick={handleFormSubmit}>Save</Button>
          <Button onClick={onClose}>Cancel</Button>
        </div>
      }
    >
      <Card heading={title}>
        <FormErrors errors={errors} />
        <Controller
          name="request_to_vacate_due_date"
          control={control}
          rules={{
            required: "Due date is required",
            validate: (value) => {
              if (!value) return "Due date is required";
              const date = STRING_TO_DATE(value);
              if (!date) return "Invalid date format";
              return true;
            },
          }}
          render={({ field: { onChange, value, ...field } }) => (
            <DatePicker
              allowFutureDates
              {...field}
              initialDate={STRING_TO_DATE(value)}
              onDateChange={(date) => {
                onChange(DATE_TO_STRING(date));
              }}
              label="Due Date"
              errors={errors}
            />
          )}
        />
        <Controller
          name="request_to_vacate_date"
          control={control}
          rules={{
            validate: (value) => {
              if (value) {
                const date = STRING_TO_DATE(value);
                if (!date) return "Invalid date format";
              }
              return true;
            },
          }}
          render={({ field: { onChange, value, ...field } }) => (
            <DatePicker
              allowFutureDates
              {...field}
              initialDate={STRING_TO_DATE(value)}
              onDateChange={(date) => {
                onChange(DATE_TO_STRING(date));
              }}
              label="Request Date"
              errors={errors}
            />
          )}
        />
        <Controller
          name="request_to_vacate_amount"
          control={control}
          rules={{
            pattern: {
              value: /^-?\d*\.?\d*$/,
              message: "Please enter a valid number",
            },
          }}
          render={({ field }) => (
            <MaskedInput
              {...field}
              mask="dollar"
              label="Amount"
              errors={errors}
            />
          )}
        />
        <Controller
          name="request_to_vacate_decision"
          control={control}
          render={({ field: { onChange, value, ...field } }) => (
            <Select
              {...field}
              label="Decision"
              options={DECISION_OPTIONS}
              value={DECISION_OPTIONS.find((option) => option.value === value)}
              onChange={(option: OptionType | null) => onChange(option?.value)}
              errors={errors}
            />
          )}
        />
        <Controller
          name="request_to_vacate_savings"
          control={control}
          rules={{
            pattern: {
              value: /^-?\d*\.?\d*$/,
              message: "Please enter a valid number",
            },
          }}
          render={({ field }) => (
            <MaskedInput
              {...field}
              mask="dollar"
              label="Savings"
              errors={errors}
            />
          )}
        />
      </Card>
    </KaboodleModal>
  );
};

export default RequestToVacateModal;
